// Frontend-side override store for RC code mappings, populated from the
// /api/rc-codes endpoint on app bootstrap. Keeps `getStyleCode()` pure (no React
// hooks) while still letting admin edits flow into POS/Quick Order code badges
// without a page reload.
//
// Usage:
//   import { loadOverrides, getOverrideCode, getOverrideLabel } from "@/data/rcCodeOverrides";
//
//   On app mount: loadOverrides();          // fire-and-forget
//   In getStyleCode():
//     const ovr = getOverrideCode(field, value);
//     if (ovr) return ovr;
//     ...fall back to static RC_CODE_LOOKUP

const state = {
  /** field -> value -> code  (extends the static map with admin-added rows) */
  codeByFieldValue: {},
  /** code -> display_label  (renamed badge for that code, if any) */
  labelByCode: {},
  loaded: false,
};

let inFlight = null;

export async function loadOverrides() {
  if (state.loaded) return state;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rc-codes`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const codeByFieldValue = {};
      const labelByCode = {};
      for (const row of data.rows || []) {
        if (!row.field || !row.value) continue;
        if (!codeByFieldValue[row.field]) codeByFieldValue[row.field] = {};
        codeByFieldValue[row.field][row.value] = row.code;
        if (row.display_label) labelByCode[row.code] = row.display_label;
      }
      state.codeByFieldValue = codeByFieldValue;
      state.labelByCode = labelByCode;
      state.loaded = true;
      window.dispatchEvent(new Event("rcOverrides:loaded"));
    } catch (e) {
      // Silent fail — static lookup will still serve dropdowns
      // eslint-disable-next-line no-console
      console.warn("RC code overrides unavailable, using static fallback:", e?.message);
    } finally {
      inFlight = null;
    }
    return state;
  })();
  return inFlight;
}

export function getOverrideCode(field, value) {
  return state.codeByFieldValue?.[field]?.[value] || null;
}

export function getOverrideLabel(code) {
  return state.labelByCode?.[code] || null;
}

/** Returns option labels that exist in the overrides for `field` but are NOT in the
 *  passed `defaultOptions`. Used by StyleSelect to render admin-added values in the
 *  dropdown without having to hard-code them in the component's option arrays. */
export function additionalFieldOptions(field, defaultOptions = []) {
  const overrides = state.codeByFieldValue?.[field];
  if (!overrides) return [];
  const known = new Set(defaultOptions.map((o) => String(o).toLowerCase()));
  return Object.keys(overrides).filter((v) => !known.has(v.toLowerCase()));
}

/** Returns the entire dynamic field → value → code map. Used by components that
 *  need to enumerate all admin-added codes (e.g. the Custom Style Codes picker). */
export function getAllOverrides() {
  return state.codeByFieldValue;
}

export function overridesLoaded() {
  return state.loaded;
}

export function subscribeOverrides(handler) {
  const wrapped = () => handler(state);
  window.addEventListener("rcOverrides:loaded", wrapped);
  return () => window.removeEventListener("rcOverrides:loaded", wrapped);
}
